import React, { useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import TagManager from 'react-gtm-module';

// Context and Services
import { useAuth } from './context/AuthContext';
import { achievementsAPI } from './services/api';
import config from './config';

// Components
import Layout from './components/Layout';
import PrivateRoute, { PublicOnlyRoute } from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import SEO from './components/SEO';
import OAuthCallback from './components/OAuthCallback';
import NotFound from './components/NotFound';
import { Onboarding } from './components/onboarding';
import CookieBanner from './components/CookieBanner';

// Public Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import CourseCreate from './pages/CourseCreate';
import CourseEdit from './pages/CourseEdit';
import LearningPaths from './pages/LearningPaths';
import LearningPathDetail from './pages/LearningPathDetail';
import LearningPathForm from './pages/LearningPathForm';
import Forum from './pages/Forum';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Private Pages
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import Dashboard from './pages/Dashboard';
import Leaderboard from './pages/Leaderboard';
import Bookmarks from './pages/Bookmarks';
import Notifications from './pages/Notifications';
import MyLearningPaths from './pages/MyLearningPaths';

// Lesson Pages
import LessonDetailPage from './pages/LessonDetailPage';
import EditLessonPage from './pages/EditLessonPage';
import LessonLibrary from './pages/LessonLibrary';
import CreateLessonPage from './pages/CreateLessonPage';
import UpdateLessonPage from './pages/UpdateLessonPage';
import LessonPage from './pages/LessonPage';
import MyLessons from './pages/MyLessons';

// Test Pages
import TestView from './components/TestView';
import TestResults from './pages/TestResults';
import TestResultDetail from './pages/TestResultDetail';
import TestPreviewPage from './pages/TestPreviewPage';
import OpenAnswerReviewPage from './pages/OpenAnswerReviewPage';

// Module Pages
import ModulesList from './pages/ModulesList';
import ModuleCreate from './pages/ModuleCreate';
import ModuleEdit from './pages/ModuleEdit';
import ModuleDetail from './pages/ModuleDetail';

// Admin Pages
import AdminDashboard from './pages/AdminDashboard';
import DatabaseBackupPage from './pages/DatabaseBackupPage';
import LessonChangeRequestsPage from './pages/LessonChangeRequestsPage';
import LessonChangeRequestDetailPage from './pages/LessonChangeRequestDetailPage';

// Lesson Change Request Pages
import RequestLessonChangesPage from './pages/RequestLessonChangesPage';
import MyLessonChangeRequestsPage from './pages/MyLessonChangeRequestsPage';

// GTM Configuration
const gtmId = config.GOOGLE_TAG_MANAGER_ID;

function App() {
  const { isAuthenticated, user: userDetails } = useAuth();
  const [achievementsChecked, setAchievementsChecked] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  // GTM and Cookie Consent Logic
  useEffect(() => {
    // Only proceed if GTM key exists
    if (!gtmId) return;

    const consent = localStorage.getItem('cookieConsent');

    if (!consent) {
      setShowCookieBanner(true);
    } else if (consent === 'true') {
      TagManager.initialize({ gtmId });
    }
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'true');
    TagManager.initialize({ gtmId });
    setShowCookieBanner(false);
  };

  // Проверка достижений при первой загрузке приложения аутентифицированным пользователем
  useEffect(() => {
    const checkAchievements = async () => {
      if (isAuthenticated && userDetails && !achievementsChecked) {
        try {
          await achievementsAPI.check();
          setAchievementsChecked(true);
        } catch (error) {
          console.error('Ошибка при проверке достижений:', error);
        }
      }
    };

    checkAchievements();
  }, [isAuthenticated, userDetails, achievementsChecked]);

  return (
    <>
      <SEO />
      {showCookieBanner && (
        <CookieBanner
          onAccept={handleAcceptCookies}
        />
      )}
      <Layout>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/register" element={<Register />} />
          <Route path="/oauth-callback" element={<OAuthCallback />} />
          <Route path="/oauth-callback/login" element={<OAuthCallback />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />

          {/* Course routes */}
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/create" element={<CourseCreate />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/courses/:slug/edit" element={<CourseEdit />} />

          {/* Learning Path routes */}
          <Route path="/learning-paths" element={<LearningPaths />} />
          <Route path="/learning-paths/my" element={<MyLearningPaths />} />
          <Route path="/learning-paths/:pathId" element={<LearningPathDetail />} />
          <Route path="/learning-paths/create" element={<LearningPathForm />} />
          <Route path="/learning-paths/:pathId/edit" element={<LearningPathForm />} />

          {/* Forum routes */}
          <Route path="/forum" element={<Forum />} />
          <Route path="/forum/:id" element={<Forum />} />

          {/* Private routes */}
          <Route
            path="/leaderboard"
            element={
              <PrivateRoute>
                <Leaderboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <PrivateRoute>
                <UserProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/lessons/bookmarks"
            element={
              <PrivateRoute>
                <Bookmarks />
              </PrivateRoute>
            }
          />
          <Route
            path="/bookmarks"
            element={
              <PrivateRoute>
                <Bookmarks />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseSlug/modules/:moduleSlug/lessons/:lessonSlug"
            element={
              <PrivateRoute>
                <LessonDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-lesson/:courseSlug/:moduleSlug/:lessonSlug"
            element={
              <PrivateRoute>
                <EditLessonPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseSlug/modules/:moduleSlug/tests/:testId/preview"
            element={
              <PrivateRoute>
                <TestPreviewPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseSlug/modules/:moduleSlug/lessons/:lessonSlug/tests/:testId"
            element={
              <PrivateRoute>
                <TestView />
              </PrivateRoute>
            }
          />
          <Route
            path="/courses/:courseSlug/modules/:moduleSlug/tests/:testId"
            element={
              <PrivateRoute>
                <TestView />
              </PrivateRoute>
            }
          />
          <Route
            path="/test-results"
            element={
              <PrivateRoute>
                <TestResults />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/test-results/:id"
            element={
              <PrivateRoute>
                <TestResultDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <Notifications />
              </PrivateRoute>
            }
          />

          {/* Lesson routes */}
          <Route 
            path="/lessons" 
            element={
              <PrivateRoute>
                <LessonLibrary />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/lessons/create" 
            element={
              <PrivateRoute>
                <CreateLessonPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/lessons/edit/:lessonSlug" 
            element={
              <PrivateRoute>
                <UpdateLessonPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/lessons/:lessonSlug" 
            element={
              <PrivateRoute>
                <LessonPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/lessons/my" 
            element={
              <PrivateRoute>
                <MyLessons />
              </PrivateRoute>
            } 
          />

          {/* Lesson Change Request routes */}
          <Route
            path="/request-lesson-changes/:lessonSlug"
            element={
              <PrivateRoute>
                <RequestLessonChangesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/my-change-requests"
            element={
              <PrivateRoute>
                <MyLessonChangeRequestsPage />
              </PrivateRoute>
            }
          />

          {/* Module routes */}
          <Route
            path="/modules"
            element={
              <PrivateRoute>
                <ModulesList />
              </PrivateRoute>
            }
          />
          <Route
            path="/modules/create"
            element={
              <PrivateRoute>
                <ModuleCreate />
              </PrivateRoute>
            }
          />
          <Route
            path="/modules/:id"
            element={
              <PrivateRoute>
                <ModuleDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/modules/:id/edit"
            element={
              <PrivateRoute>
                <ModuleEdit />
              </PrivateRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/database-backup"
            element={
              <AdminRoute>
                <DatabaseBackupPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/open-answer-review"
            element={
              <PrivateRoute>
                <OpenAnswerReviewPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tests/:testId"
            element={
              <AdminRoute>
                <TestPreviewPage />
              </AdminRoute>
            }
          />
          <Route
            path="/lesson-change-requests"
            element={
              <AdminRoute>
                <LessonChangeRequestsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/lesson-change-requests/:requestId"
            element={
              <AdminRoute>
                <LessonChangeRequestDetailPage />
              </AdminRoute>
            }
          />

          {/* 404 - Catch all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Onboarding />
      </Layout>
    </>
  );
}

export default App;
